// FieldDesk — экран «Клиенты» (Sprint 7).
//
// Левая панель (300px): поиск + список с totalVisits + CSV-экспорт.
// Правая часть: Leaflet-карта (OSM) с маркерами клиентов (зелёные кружки;
//   выбранный — крупнее и оранжевый). Под картой — таблица истории визитов
//   выбранного клиента с computed-полем lateMinutes.
import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { useAuth } from '../auth/AuthContext';
import type { ClientDto, ClientVisitHistoryRow, VisitDto } from '../api/types';
import { ErrorStrip } from './Dashboard';
import { STATUS_SCHEMA, TYPE_COLOR, TYPE_LABEL } from '../styles/theme';

const BISHKEK: [number, number] = [42.8746, 74.5698];

// Список типов торговой точки строится динамически из загруженных клиентов
// (уникальные непустые значения storeType, отсортированные по алфавиту).
// Больше не нужен хардкод — маппинг теперь живёт на бэкенде через
// crm.company.fields Битрикса.

// Sprint 7-task 4: ссылка на карточку компании в Б24.
// Домен берём из VITE_BITRIX_PORTAL_DOMAIN, fallback — softvision.bitrix24.kz.
const BITRIX_PORTAL_DOMAIN =
  (import.meta.env.VITE_BITRIX_PORTAL_DOMAIN as string | undefined)?.trim() ||
  'softvision.bitrix24.kz';

function bitrixCompanyUrl(bitrixCompanyId: string | null): string | null {
  if (!bitrixCompanyId || bitrixCompanyId === '0') return null;
  return `https://${BITRIX_PORTAL_DOMAIN}/crm/company/details/${bitrixCompanyId}/`;
}

interface Props {
  // Для будущего: переход с истории клиента на детальную карточку выезда.
  // Сейчас не используется (таблица — read-only).
  onOpenVisit?(v: VisitDto): void;
}

export function ClientsScreen({ onOpenVisit: _onOpenVisit }: Props) {
  const { api, state } = useAuth();
  const user = state.status === 'authenticated' ? state.user : null;
  // Смена статуса клиента — permission clients.update: ADMIN/DISPATCHER/ENGINEER
  // (фронт зеркалит backend-матрицу из permissions.ts).
  const canUpdateClient = user != null;
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [storeTypeFilter, setStoreTypeFilter] = useState<string>(''); // '' = все
  const [statusFilter, setStatusFilter] = useState<'active' | 'passive' | ''>('active');
  const [selected, setSelected] = useState<ClientDto | null>(null);
  const [history, setHistory] = useState<ClientVisitHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Leaflet — карта живёт imperative-ом, как в NewTaskModal/Map.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<number, L.CircleMarker>>(new Map());

  // --- Загрузка клиентов (перезагружается при смене statusFilter) -----------
  useEffect(() => {
    setLoading(true);
    setSelected(null); // сбрасываем выбор при смене фильтра
    const params: { limit: number; status?: string } = { limit: 200 };
    if (statusFilter) params.status = statusFilter;
    api
      .listClients(params)
      .then((r) => setClients(r.items))
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [api, statusFilter]);

  // --- История выездов выбранного клиента -----------------------------------
  useEffect(() => {
    if (!selected) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    api
      .listClientVisits(selected.id)
      .then((r) => setHistory(r.items))
      .catch((e: Error) => setErr(e.message))
      .finally(() => setHistoryLoading(false));
  }, [api, selected]);

  // --- Init карта (один раз) ------------------------------------------------
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current).setView(BISHKEK, 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    // На случай если контейнер ещё не получил финальный размер.
    setTimeout(() => map.invalidateSize(), 50);
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
  }, []);

  // --- Динамический список типов торговой точки -----------------------------
  // Берём уникальные непустые storeType из загруженных клиентов.
  const storeTypes = useMemo(() => {
    const seen = new Set<string>();
    for (const c of clients) {
      if (c.storeType) seen.add(c.storeType);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [clients]);

  // --- Фильтрация по поиску + типу торговой точки --------------------------
  // (вынесена ВЫШЕ markers-useEffect, потому что маркеры теперь рисуем
  //  только для отфильтрованных клиентов — иначе фильтр работал только в
  //  списке, а на карте оставались все маркеры).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (storeTypeFilter && (c.storeType ?? '') !== storeTypeFilter) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        (c.shortName ?? '').toLowerCase().includes(q) ||
        (c.address ?? '').toLowerCase().includes(q)
      );
    });
  }, [clients, search, storeTypeFilter]);

  // --- Маркеры — пересоздаются при изменении filtered -----------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Чистим старые.
    markersRef.current.forEach((m) => m.remove());
    markersRef.current.clear();

    const withGeo = filtered.filter(
      (c) => c.latitude != null && c.longitude != null,
    );
    withGeo.forEach((c) => {
      const isPassive = c.status === 'passive';
      const m = L.circleMarker([c.latitude!, c.longitude!], {
        radius: 7,
        fillColor: isPassive ? '#94a3b8' : '#16a34a',
        color: isPassive ? '#64748b' : '#15803d',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.85,
      });
      // --- Popup по клику: полная инфа + ссылка на Б24 ---
      const bxUrl = bitrixCompanyUrl(c.bitrixCompanyId);
      const popupHtml =
        `<div style="min-width:180px"><div style="font-weight:600;font-size:13px;color:#0f172a">${escapeHtml(c.name)}</div>` +
        (c.address ? `<div style="font-size:11px;color:#64748b;margin-top:4px">${escapeHtml(c.address)}</div>` : '') +
        (c.storeType ? `<div style="font-size:11px;color:#475569;margin-top:4px">Тип: <b>${escapeHtml(c.storeType)}</b></div>` : '') +
        `<div style="font-size:11px;color:#16a34a;margin-top:6px;font-weight:600">📍 ${c.totalVisits ?? 0} выезд(ов)</div>` +
        (bxUrl
          ? `<div style="margin-top:8px"><a href="${bxUrl}" target="_blank" rel="noopener noreferrer" style="color:#2563eb;font-size:11px;font-weight:600;text-decoration:none">🔗 Открыть в Битрикс24</a></div>`
          : '') +
        '</div>';
      m.bindPopup(popupHtml);
      // --- Tooltip по hover: имя + тип точки ---
      const tooltipText = c.storeType
        ? `${escapeHtml(c.name)} · ${escapeHtml(c.storeType)}`
        : escapeHtml(c.name);
      m.bindTooltip(tooltipText, { direction: 'top', offset: [0, -10], opacity: 0.95 });

      m.on('click', () => setSelected(c));
      m.addTo(map);
      markersRef.current.set(c.id, m);
    });

    // Auto-fit, чтобы видно было всех отфильтрованных клиентов с координатами.
    if (withGeo.length > 1) {
      const bounds = L.latLngBounds(
        withGeo.map((c) => [c.latitude!, c.longitude!] as [number, number]),
      );
      map.fitBounds(bounds.pad(0.2));
    }
  }, [filtered]);

  // --- Подсветка выбранного маркера -----------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((m, id) => {
      if (selected && id === selected.id) {
        m.setStyle({ radius: 11, fillColor: '#f97316', color: '#c2410c', weight: 3 });
        m.bringToFront();
      } else {
        // Восстанавливаем цвет по статусу клиента (если он есть в filtered)
        const client = filtered.find((c) => c.id === id);
        const isPassive = client?.status === 'passive';
        m.setStyle({
          radius: 7,
          fillColor: isPassive ? '#94a3b8' : '#16a34a',
          color: isPassive ? '#64748b' : '#15803d',
          weight: 2,
        });
      }
    });
    // Центрируем карту на выбранном клиенте.
    if (selected && selected.latitude != null && selected.longitude != null) {
      map.setView([selected.latitude, selected.longitude], Math.max(map.getZoom(), 14));
    }
  }, [selected]);

  // Старый `filtered` тут жил — перенесён ВЫШЕ, к markers-useEffect,
  // потому что и список, и маркеры используют один и тот же набор.

  function handleToggleStatus() {
    if (!selected || statusUpdating) return;
    const newStatus = selected.status === 'passive' ? 'active' : 'passive';
    setStatusUpdating(true);
    api
      .updateClientStatus(selected.id, newStatus)
      .then((updated) => {
        // Обновляем клиента в списке и в selected
        setClients((prev) => prev.map((c) => (c.id === updated.id ? { ...c, status: updated.status as 'active' | 'passive' } : c)));
        setSelected((prev) => (prev ? { ...prev, status: updated.status as 'active' | 'passive' } : prev));
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setStatusUpdating(false));
  }

  function handleExport() {
    const headers = [
      'id',
      'name',
      'short_name',
      'address',
      'latitude',
      'longitude',
      'bitrix_company_id',
      'total_visits',
      'status',
    ];
    const rows = clients.map((c) =>
      [
        c.id,
        csvCell(c.name),
        csvCell(c.shortName ?? ''),
        csvCell(c.address ?? ''),
        c.latitude ?? '',
        c.longitude ?? '',
        csvCell(c.bitrixCompanyId ?? ''),
        c.totalVisits ?? 0,
        c.status ?? 'active',
      ].join(','),
    );
    // ﻿ — BOM, чтобы Excel правильно понял UTF-8.
    const csv = '﻿' + [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clients.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* === Left pane: search + list + export === */}
      <div
        style={{
          width: 300,
          flexShrink: 0,
          background: '#fff',
          borderRight: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: 16, borderBottom: '1px solid #e2e8f0' }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Клиенты</h1>
          <button
            onClick={handleExport}
            disabled={clients.length === 0}
            style={{
              width: '100%',
              padding: '8px 12px',
              background: clients.length === 0 ? '#cbd5e1' : '#16a34a',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: clients.length === 0 ? 'not-allowed' : 'pointer',
              marginBottom: 10,
            }}
          >
            ⬇ Экспорт CSV ({clients.length})
          </button>
          <input
            type="text"
            placeholder="🔍 Поиск по названию или адресу"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              fontSize: 13,
              boxSizing: 'border-box',
              marginBottom: 8,
            }}
          />
          {/* Фильтр по типу торговой точки — список строится из данных */}
          <select
            value={storeTypeFilter}
            onChange={(e) => setStoreTypeFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              fontSize: 13,
              boxSizing: 'border-box',
              background: '#fff',
              cursor: 'pointer',
              marginBottom: 8,
            }}
          >
            <option value="">Тип точки: все ({clients.length})</option>
            {storeTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {/* Фильтр по статусу */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'active' | 'passive' | '')}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              fontSize: 13,
              boxSizing: 'border-box',
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            <option value="active">Статус: активные</option>
            <option value="passive">Статус: пассивные</option>
            <option value="">Статус: все</option>
          </select>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {err && (
            <div style={{ padding: 12 }}>
              <ErrorStrip message={err} />
            </div>
          )}
          {loading ? (
            <div style={{ padding: 20, color: '#94a3b8', fontSize: 13 }}>Загрузка…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 20, color: '#94a3b8', fontSize: 13 }}>
              {search ? 'Никого не найдено по запросу.' : 'Клиентов пока нет.'}
            </div>
          ) : (
            filtered.map((c) => {
              const isPassive = c.status === 'passive';
              return (
              <div
                key={c.id}
                style={{
                  background: selected?.id === c.id ? '#eff6ff' : 'transparent',
                  borderLeft:
                    selected?.id === c.id ? '3px solid #2563eb' : '3px solid transparent',
                  borderBottom: '1px solid #f1f5f9',
                  opacity: isPassive ? 0.6 : 1,
                }}
              >
              <button
                onClick={() => setSelected(c)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: isPassive ? '#94a3b8' : '#0f172a',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                    }}
                  >
                    {c.name}
                  </div>
                  {isPassive && (
                    <span style={{ fontSize: 10, color: '#94a3b8', background: '#f1f5f9', borderRadius: 4, padding: '1px 5px', flexShrink: 0, fontWeight: 600 }}>
                      пассивный
                    </span>
                  )}
                </div>
                {c.address && (
                  <div
                    style={{
                      fontSize: 11,
                      color: isPassive ? '#cbd5e1' : '#64748b',
                      marginTop: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={c.address}
                  >
                    📍 {c.address}
                  </div>
                )}
                {c.storeType && (
                  <div style={{ fontSize: 11, color: isPassive ? '#cbd5e1' : '#94a3b8', marginTop: 2 }}>
                    🏬 {c.storeType}
                  </div>
                )}
                <div style={{ fontSize: 11, color: isPassive ? '#cbd5e1' : '#16a34a', marginTop: 4, fontWeight: 600 }}>
                  {c.totalVisits ?? 0} выезд(ов)
                </div>
              </button>
              {/* Sprint 7-task 4: ссылка «Открыть в Б24». ВНЕ button (валидный
                  HTML — линки нельзя вкладывать в кнопки), показываем под
                  карточкой только для выбранного клиента + bitrix_company_id. */}
              {selected?.id === c.id && bitrixCompanyUrl(c.bitrixCompanyId) && (
                <a
                  href={bitrixCompanyUrl(c.bitrixCompanyId)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    margin: '0 14px 10px',
                    padding: '5px 10px',
                    background: '#eff6ff',
                    color: '#2563eb',
                    border: '1px solid #bfdbfe',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  🔗 Открыть в Битрикс24 →
                </a>
              )}
              {/* Кнопка смены статуса — permission clients.update, только для выбранного */}
              {canUpdateClient && selected?.id === c.id && (
                <div style={{ margin: '0 14px 10px' }}>
                  <button
                    onClick={handleToggleStatus}
                    disabled={statusUpdating}
                    style={{
                      padding: '5px 12px',
                      border: 'none',
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: statusUpdating ? 'not-allowed' : 'pointer',
                      background: selected.status === 'passive' ? '#dcfce7' : '#f1f5f9',
                      color: selected.status === 'passive' ? '#16a34a' : '#64748b',
                    }}
                  >
                    {statusUpdating
                      ? '…'
                      : selected.status === 'passive'
                        ? '✓ Активировать'
                        : '⏸ Перевести в пассивные'}
                  </button>
                </div>
              )}
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* === Right pane: map + history === */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div ref={containerRef} style={{ flex: selected ? '1 1 50%' : 1, minHeight: 300 }} />

        {selected && (
          <div
            style={{
              flex: '0 1 50%',
              minHeight: 200,
              borderTop: '1px solid #e2e8f0',
              background: '#f8fafc',
              padding: 16,
              overflow: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700 }}>
                История · {selected.name}
              </h2>
              <button
                onClick={() => setSelected(null)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: 16, cursor: 'pointer' }}
                title="Закрыть"
              >
                ✕
              </button>
            </div>

            {historyLoading ? (
              <div style={{ color: '#94a3b8', fontSize: 13 }}>Загрузка…</div>
            ) : history.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 13 }}>Выездов пока нет.</div>
            ) : (
              <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead style={{ background: '#f1f5f9' }}>
                    <tr>
                      <Th>Дата</Th>
                      <Th>Тип</Th>
                      <Th>Инженер</Th>
                      <Th>Статус</Th>
                      <Th>Опоздание</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((v) => {
                      const color = TYPE_COLOR[v.type] ?? '#64748b';
                      const status = STATUS_SCHEMA[v.status];
                      return (
                        <tr key={v.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <Td>
                            {formatIsoDate(v.scheduledDate)} · {v.scheduledStart}–{v.scheduledEnd}
                          </Td>
                          <Td>
                            <span
                              style={{
                                padding: '2px 7px',
                                borderRadius: 999,
                                fontSize: 11,
                                fontWeight: 600,
                                background: color + '18',
                                color,
                              }}
                            >
                              {TYPE_LABEL[v.type] ?? v.typeLabel}
                            </span>
                          </Td>
                          <Td>{v.engineerName}</Td>
                          <Td>
                            {status ? (
                              <span
                                style={{
                                  padding: '2px 7px',
                                  borderRadius: 999,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: status.bg,
                                  color: status.color,
                                }}
                              >
                                {status.label}
                              </span>
                            ) : (
                              v.statusLabel
                            )}
                          </Td>
                          <Td>
                            {v.lateMinutes > 0 ? (
                              <span style={{ color: '#dc2626', fontWeight: 600 }}>
                                +{v.lateMinutes} мин
                              </span>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>—</span>
                            )}
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 10px',
        fontSize: 11,
        fontWeight: 600,
        color: '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td style={{ padding: '8px 10px', color: '#0f172a' }}>{children}</td>
  );
}

function formatIsoDate(iso: string): string {
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

// CSV-cell: оборачиваем в кавычки если есть запятая/кавычка/перевод строки;
// внутренние кавычки удваиваем (RFC 4180).
function csvCell(v: string): string {
  if (v === '') return '';
  if (/[",\r\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}
